import React, { useEffect, useState, useCallback, useRef } from "react";

import { CircularProgress, Box } from "@mui/material";
import Alert from "@mui/material/Alert";

// components
import SearchInput from "../../components/common/searchInput";

import { getAllUsers } from "../../setup/api/services/user";
import {
  usersList,
  ALERT,
  SEARCH_MIN_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from "./constants";

import UserListItem from "./UserListItem";

import "./styles.css";
import styles from "./user_management_styles.module.scss";
import { ALERTMESSAGE } from "../../enums";
import Paginations from "../../components/common/Pagination/paginations";

interface UsersManagementProps {
  isAdmin?: boolean;
}

const getPageFromHash = () =>
  Number(window.location.hash.replace(/#/g, "")) || 1;

const UsersManagement = ({ isAdmin }: UsersManagementProps) => {
  const [page, setPage] = useState(getPageFromHash);
  const [pagesCount, setPagesCount] = useState(1);

  const pageSize = 25;
  const [loading, setLoading] = useState<boolean>(false);
  const [listUsers, setListUsers] = useState([]);
  const [alert, setAlert] = useState<ALERT>(ALERT.NONE);
  // raw text in the search box
  const [searchValue, setSearchValue] = useState("");
  // debounced keyword actually sent to the API ("" when under the min length)
  const [searchKeyword, setSearchKeyword] = useState("");
  const abortControllerRef = useRef<AbortController>();

  const trimmedSearchLength = searchValue.trim().length;
  const showMinLengthHint =
    trimmedSearchLength > 0 && trimmedSearchLength < SEARCH_MIN_LENGTH;

  const fetchUsersList = useCallback(
    async (page) => {
      // cancel the previous request so a slow, stale search can't overwrite newer results
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await getAllUsers(
          page - 1,
          pageSize,
          searchKeyword,
          controller.signal
        );
        setListUsers(response.data.content);
        setPagesCount(response.data.totalPages);
      } catch (error) {
        if (!controller.signal.aborted) console.error(error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [searchKeyword]
  );

  useEffect(() => {
    const handleHashChange = () => setPage(getPageFromHash());
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = searchValue.trim();
      const nextKeyword = trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : "";
      if (nextKeyword === searchKeyword) return;

      // a new keyword always starts from the first page
      setSearchKeyword(nextKeyword);
      setPage(1);
      window.history.replaceState(null, "", "#1");
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue, searchKeyword]);

  useEffect(() => {
    fetchUsersList(page);
  }, [fetchUsersList, page]);

  const handlePaginationChange = (value: number) => {
    window.location.hash = `${value}`;
    setLoading(true);
  };

  if (loading)
    return (
      <Box className="container center">
        <CircularProgress />
      </Box>
    );

  return (
    <div className={styles["page_layout"]}>
      {alert !== ALERT.NONE && alert !== ALERT.SUCCESS && (
        <Alert severity={"error"}>{ALERTMESSAGE.ERROR}</Alert>
      )}
      {alert === ALERT.SUCCESS && (
        <Alert severity={"success"}>{ALERTMESSAGE.SUCCESS}</Alert>
      )}
      <h1>{isAdmin ? usersList.WAFFY_USERS : usersList.USERS}</h1>
      <div className={styles.header_container}>
        <h3>{usersList.USERS_LIST}</h3>
        <div className={styles.search_container}>
          <SearchInput
            dir="ltr"
            placeholder={usersList.FIND_A_USER}
            value={searchValue}
            handleChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
          {showMinLengthHint && (
            <p className={styles.search_hint}>
              {usersList.SEARCH_MIN_LENGTH_HINT}
            </p>
          )}
        </div>
      </div>
      <table className={styles.table}>
        <tr className={styles.table_header}>
          <th className={styles.hint}>{usersList.CUSTOMER_NAME}</th>
          <th className={styles.hint}>{usersList.MOBILE_NUMBER}</th>
          <th className={styles.hint}>
            {isAdmin ? usersList.PREACH : usersList.DOCUMENTATION}
          </th>
          <th className={styles.hint}>{usersList.THE_CONDITION}</th>
          <th className={styles.hint}>{usersList.DATE_CREATED}</th>
          <th className={styles.hint}></th>
          <th className={styles.hint}></th>
        </tr>
        <tbody className={styles.table_body}>
          {listUsers.map((userItem, index) => (
            <UserListItem
              key={index}
              item={userItem}
              fetchUsersList={fetchUsersList}
              page={page}
              alert={setAlert}
              loading={setLoading}
            />
          ))}
        </tbody>
      </table>

      <>
        <div className={listUsers.length ? "paginatorContainer" : "hide"}>
          <Paginations
            pagesCount={pagesCount}
            length={pagesCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </>
    </div>
  );
};
export default UsersManagement;
